/**
 * NotificationBell — Wave 8 (FRONTEND).
 *
 * Bell icon mounted in `TopBar.shell__topbar-actions`. Renders a small
 * unread-count badge over the icon and, on click, opens a 380px popover
 * with the most recent 20 in-app notifications for the current officer.
 *
 * Polling:
 *   - Default 30s while the tab is visible.
 *   - Suspended when `document.hidden === true` (Page Visibility API);
 *     resumes immediately on visibilitychange. Bill 2026-04-25 binding —
 *     "polls but doesn't background-fetch on tab-hidden".
 *   - Polling interval may eventually become admin-tunable; for now the
 *     30s default lives in the constant below.
 *
 * Auth:
 *   - The server resolves UPN from the bearer token when the client sends
 *     `upn: 'me'`. SuperAdmin "view-as" is out of scope for v3.0.
 *
 * No GUIDs are surfaced in the UI: notification IDs travel only on the
 * mark-read / dismiss / navigate API calls.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import type { InAppNotification, Iso8601 } from '@retail-ops/api-client';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { NotificationCard } from './NotificationCard';
import './NotificationBell.css';

/** 30s default — admin-tunable surface lives in SystemSettings.notifications.* */
const POLL_INTERVAL_MS = 30_000;
/** Top of the most recent N rows — design §10. */
const DEFAULT_TOP = 20;

export interface NotificationBellProps {
  /** Optional override for the poll interval (ms). Default 30000. */
  pollMs?: number;
  /** Called with `notification.navigateTo` when a row is activated. */
  onNavigate?: (path: string, notification: InAppNotification) => void;
}

/**
 * Best-effort default navigator: pushes the in-app deep-link path to the
 * SPA history when no parent handler is supplied. Avoids react-router
 * coupling at this layer (the bell mounts inside the topbar which is
 * router-agnostic).
 */
function defaultNavigate(path: string): void {
  if (!path) return;
  if (typeof window === 'undefined') return;
  // Use HTML5 history so SPA routers (react-router) pick up the change.
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function NotificationBell({
  pollMs = POLL_INTERVAL_MS,
  onNavigate,
}: NotificationBellProps): JSX.Element {
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ReadonlyArray<InAppNotification>>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const pollHandleRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const fetchOnce = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const resp = await client.retail.notifications.inApp.list(
          { upn: 'me', top: DEFAULT_TOP },
          signal ? { signal } : undefined
        );
        if (!isMountedRef.current) return;
        if (resp.success) {
          setRows(resp.rows ?? []);
          setUnreadCount(resp.unreadCount ?? 0);
          setErrorMsg(null);
        } else {
          setErrorMsg(resp.error ?? 'Could not load notifications.');
        }
      } catch (e: unknown) {
        if (!isMountedRef.current) return;
        // Aborted polls are normal during teardown — never surface.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setErrorMsg(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e)
        );
      }
    },
    [client]
  );

  // ── Visibility-aware polling ─────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    const ctrl = new AbortController();

    const tick = (): void => {
      // Visibility-aware: skip the network when the tab is hidden. The
      // visibilitychange handler below schedules an immediate refetch on
      // resume so the badge reflects new arrivals as soon as the user
      // comes back.
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchOnce(ctrl.signal);
    };

    const startPolling = (): void => {
      if (pollHandleRef.current !== null) return;
      pollHandleRef.current = window.setInterval(tick, pollMs);
    };
    const stopPolling = (): void => {
      if (pollHandleRef.current !== null) {
        window.clearInterval(pollHandleRef.current);
        pollHandleRef.current = null;
      }
    };

    const onVisChange = (): void => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Immediate refetch on resume + restart the interval.
        void fetchOnce(ctrl.signal);
        startPolling();
      }
    };

    // Kick a first fetch immediately so the badge populates at mount.
    void fetchOnce(ctrl.signal);
    startPolling();
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      isMountedRef.current = false;
      stopPolling();
      document.removeEventListener('visibilitychange', onVisChange);
      ctrl.abort();
    };
  }, [fetchOnce, pollMs]);

  // ── Outside-click closes the popover ─────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // ── Action handlers ──────────────────────────────────────────────────
  const handleMarkRead = useCallback(
    async (id: string): Promise<void> => {
      // Optimistic update — flip readAt locally so the badge ticks down
      // instantly. Server is the source of truth; we re-fetch on next poll.
      const nowIso = new Date().toISOString() as Iso8601;
      setRows((prev) =>
        prev.map((r) => (r.id === id && !r.readAt ? { ...r, readAt: nowIso } : r))
      );
      setUnreadCount((n) => Math.max(0, n - 1));
      try {
        await client.retail.notifications.inApp.markRead(id);
      } catch {
        // On failure, refetch authoritative state.
        void fetchOnce();
      }
    },
    [client, fetchOnce]
  );

  const handleDismiss = useCallback(
    async (id: string): Promise<void> => {
      const dismissedRow = rows.find((r) => r.id === id);
      const wasUnread = dismissedRow !== undefined && !dismissedRow.readAt;
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (wasUnread) setUnreadCount((n) => Math.max(0, n - 1));
      try {
        await client.retail.notifications.inApp.dismiss(id);
      } catch {
        void fetchOnce();
      }
    },
    [client, rows, fetchOnce]
  );

  // PILOT 2026-09-04: this used to call inApp.markAllRead(), which posts to
  // /v3/notifications/in-app/mark-all-read - A ROUTE THAT DOES NOT EXIST on the
  // server (only {id}/mark-read and {id}/dismiss do). Every click 404'd, the
  // catch silently refetched, and the badge came straight back. The bell is on
  // every page including the pilot rail, so it was a visible broken control.
  //
  // Fixed against the route that DOES exist rather than by adding an endpoint:
  // the decomposition stop-growing rule sends new notification routes to
  // pcb-platform-hub, which does not exist yet, and this is a UI defect, not a
  // missing capability. The popover holds one page of recent notifications, so
  // marking them individually is a handful of calls, issued in parallel.
  const handleMarkAllRead = useCallback(async (): Promise<void> => {
    const unreadIds = rows.filter((r) => !r.readAt).map((r) => r.id);
    if (unreadIds.length === 0) return;

    const nowIso = new Date().toISOString() as Iso8601;
    setRows((prev) => prev.map((r) => (r.readAt ? r : { ...r, readAt: nowIso })));
    setUnreadCount(0);

    const results = await Promise.allSettled(
      unreadIds.map((id) => client.retail.notifications.inApp.markRead(id))
    );
    // Any failure means the optimistic state is now a lie - resync from the
    // server rather than leave the badge showing zero incorrectly.
    if (results.some((r) => r.status === 'rejected')) void fetchOnce();
  }, [client, rows, fetchOnce]);

  const handleNavigate = useCallback(
    (n: InAppNotification): void => {
      setOpen(false);
      if (!n.navigateTo) return;
      if (onNavigate) onNavigate(n.navigateTo, n);
      else defaultNavigate(n.navigateTo);
    },
    [onNavigate]
  );

  const handleToggle = useCallback((): void => {
    setOpen((v) => {
      const next = !v;
      // When opening, refresh once for fresh state.
      if (next) {
        setLoading(true);
        void fetchOnce().finally(() => {
          if (isMountedRef.current) setLoading(false);
        });
      }
      return next;
    });
  }, [fetchOnce]);

  const badgeText = useMemo(() => {
    if (unreadCount <= 0) return '';
    if (unreadCount > 9) return '9+';
    return String(unreadCount);
  }, [unreadCount]);

  const ariaLabel = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications, no unread';

  return (
    <div className="notif-bell" data-tour="topbar-notifications" ref={wrapperRef}>
      <button
        type="button"
        className="notif-bell__btn"
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
      >
        <Bell size={16} aria-hidden />
        {badgeText && (
          <span className="notif-bell__badge" aria-hidden>
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-bell__pop" role="dialog" aria-label="Notifications">
          <header className="notif-bell__pop-header">
            <h2 className="notif-bell__pop-title">Notifications</h2>
            <button
              type="button"
              className="notif-bell__mark-all"
              onClick={() => void handleMarkAllRead()}
              disabled={unreadCount === 0}
            >
              Mark all read
            </button>
          </header>

          {errorMsg && (
            <div className="notif-bell__loading" role="status">
              {errorMsg}
            </div>
          )}

          {loading && rows.length === 0 && !errorMsg && (
            <div className="notif-bell__loading">Loading…</div>
          )}

          {!loading && !errorMsg && rows.length === 0 && (
            <div className="notif-bell__empty">
              <span className="notif-bell__empty-icon" aria-hidden>📭</span>
              You&apos;re all caught up.
            </div>
          )}

          {rows.length > 0 && (
            <ul className="notif-bell__list">
              {rows.map((n) => (
                <li key={n.id} className="notif-bell__list-item">
                  <NotificationCard
                    notification={n}
                    onNavigate={handleNavigate}
                    onMarkRead={(id) => void handleMarkRead(id)}
                    onDismiss={(id) => void handleDismiss(id)}
                  />
                </li>
              ))}
            </ul>
          )}

          <footer className="notif-bell__footer">
            Showing {rows.length} most recent
          </footer>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
